/**
 * Date utility functions for consistent date formatting across the app
 * All dates are displayed in DD-MM-YYYY format
 */

/**
 * Format a date object to YYYY-MM-DD string (for database storage)
 * @param {Date} date - The date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForDB = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d?.getFullYear();
  const month = String(d?.getMonth() + 1)?.padStart(2, '0');
  const day = String(d?.getDate())?.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a date to DD-MM-YYYY string (for display)
 * @param {Date|string} date - The date to format
 * @returns {string} Date in DD-MM-YYYY format
 */
export const formatDateForDisplay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d?.getFullYear();
  const month = String(d?.getMonth() + 1)?.padStart(2, '0');
  const day = String(d?.getDate())?.padStart(2, '0');
  return `${day}-${month}-${year}`;
};

/**
 * Format a date to DD-MM-YYYY with day name (e.g., "Mon, 10-02-2026")
 * @param {Date|string} date - The date to format
 * @returns {string} Date with day name
 */
export const formatDateWithDay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const dayName = d?.toLocaleDateString('en-US', { weekday: 'short' });
  return `${dayName}, ${formatDateForDisplay(d)}`;
};

/**
 * Format a date to month name and year (e.g., "February 2026")
 * @param {Date|string} date - The date to format
 * @returns {string} Month and year
 */
export const formatMonthYear = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/**
 * Format a date range (e.g., "10-02-2026 - 16-02-2026")
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {string} Date range
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
};

/**
 * Format a week range with month names (e.g., "Feb 10-16, 2026" or "Jan 30 - Feb 5, 2026")
 * @param {Date} weekStart - Start of the week
 * @returns {string} Week range
 */
export const formatWeekRange = (weekStart) => {
  if (!weekStart) return '';
  const endDate = new Date(weekStart);
  endDate?.setDate(endDate?.getDate() + 6);
  
  const startMonth = weekStart?.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = endDate?.toLocaleDateString('en-US', { month: 'short' });
  const year = weekStart?.getFullYear();
  
  if (startMonth === endMonth) {
    return `${startMonth} ${weekStart?.getDate()}-${endDate?.getDate()}, ${year}`;
  }
  return `${startMonth} ${weekStart?.getDate()} - ${endMonth} ${endDate?.getDate()}, ${year}`;
};

/**
 * Check if a date is today
 * @param {Date|string} date - The date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  const d = new Date(date);
  const today = new Date();
  return d?.toDateString() === today?.toDateString();
};

/**
 * Normalize a date from database (handles both DATE and TIMESTAMP formats)
 * @param {string} dateStr - Date string from database
 * @returns {string} Date in YYYY-MM-DD format
 */
export const normalizeDateFromDB = (dateStr) => {
  if (!dateStr) return '';
  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/?.test(dateStr)) {
    return dateStr;
  }
  // If it contains timestamp, extract date part
  return dateStr?.split('T')?.[0] || dateStr;
};

/**
 * Format time without seconds (HH:MM:SS → HH:MM)
 * @param {string} time - Time string in HH:MM:SS format
 * @returns {string} Time in HH:MM format
 */
export const formatTimeWithoutSeconds = (time) => {
  if (!time) return '';
  return time?.slice(0, 5);
};

/**
 * Calculate end time from start time and duration
 * @param {string} startTime - Start time in HH:MM:SS or HH:MM format
 * @param {number} duration - Duration in minutes
 * @returns {string} End time in HH:MM format
 */
export const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return '';
  
  // Parse start time
  const [hours, minutes] = startTime?.split(':')?.map(Number);
  
  // Create a date object for calculation
  const date = new Date();
  date?.setHours(hours, minutes, 0, 0);
  
  // Add duration in minutes
  date?.setMinutes(date?.getMinutes() + duration);
  
  // Format as HH:MM
  const endHours = String(date?.getHours())?.padStart(2, '0');
  const endMinutes = String(date?.getMinutes())?.padStart(2, '0');
  
  return `${endHours}:${endMinutes}`;
};